import type { ManualEntryContext, ManualEntrySource } from "../types/manualEntry";
export const TRANSPORT_ENTRY_ROLES = ["学生", "老师", "司机"] as const;
export type TransportEntryField = "from" | "to" | "role" | "people" | "price";
export type TransportEntryRow = { record: ManualEntrySource; partIndex: number; from: string; to: string;
  role: string; people: string; price: string; linked: boolean; editable: boolean };
const roleKeys = { 学生: "studentCount", 老师: "teacherCount", 司机: "driverCount" } as const;
const rolePattern = /^(学生|老师|导游|司机|司陪|角色)\s*/u;
const valueOf = (value = "") => /^(出发城市|到达城市|角色|人数|单价)$/.test(value) ? "" : value;
const roleCount = (role: string, context: ManualEntryContext) => role in roleKeys ? String(context.people[roleKeys[role as keyof typeof roleKeys]] ?? 0) : "";

/** 只提取填写字段，合法城市与金额仍由原解析器校验。逗号沿用路线，换行重置。 */
function partsOf(text: string) {
  let from = "", to = "";
  return text.normalize("NFKC").split(/[,，]/u).map(part => {
    const raw = part.trim(); let body = raw;
    const explicit = !rolePattern.test(raw);
    if (explicit) {
      from = ""; to = "";
      const route = raw.match(/^(.+?)\s*[-—→]\s*(.+?)(?=\s*(?:学生|老师|导游|司机|司陪|角色)|$)/u);
      if (route) { from = route[1].trim(); to = route[2].trim(); body = raw.slice(route[0].length).trim(); }
    }
    // 空片段保留为待修正记录，不能因表格修改而静默删除。
    const standalone = explicit ? raw || "待补充费用" : from && to ? `${from}-${to} ${raw}` : raw;
    return { from, to, body, standalone };
  });
}

/** 读取飞机或船的路线与角色费用，保留未完成原文。 */
export function transportEntryRows(text: string, context: ManualEntryContext): TransportEntryRow[] {
  return [...text.matchAll(/[^\n;；]+/gu)].filter(match => match[0].trim()).flatMap(match => {
    const start = (match.index ?? 0) + match[0].search(/\S/u), raw = match[0].trim();
    const record = { text: raw, start, end: start + raw.length, line: text.slice(0, start).split("\n").length };
    return partsOf(raw).map((part, partIndex) => {
      const role = part.body.match(rolePattern);
      const factors = part.body.slice(role?.[0].length ?? 0).replace(/\s/gu, "").split(/[xX×*]/u)
        .map(value => value.replace(/(?<=\d)(?:人|元)$/u, ""));
      const linked = factors.length === 1, name = valueOf(role?.[1]);
      return { record, partIndex, from: valueOf(part.from), to: valueOf(part.to), role: name, linked,
        people: linked ? roleCount(name, context) : valueOf(factors[0]), price: valueOf(factors.at(-1)),
        editable: Boolean(role && factors.length <= 2) };
    });
  });
}

/** 固化同记录其他行的原路线，修改当前行不会改变后续角色的归属。 */
export function changeTransportEntryRow(text: string, row: TransportEntryRow, field: TransportEntryField, value: string, context: ManualEntryContext): string {
  if (!row.editable || /[\n\r;；,，×*]/u.test(value)) return text;
  if (["people", "price"].includes(field) && /[xX]/u.test(value)) return text;
  if (["from", "to"].includes(field) && /[-—→]|学生|老师|导游|司机|司陪|角色/u.test(value)) return text;
  if (field === "role" && !(TRANSPORT_ENTRY_ROLES as readonly string[]).includes(value)) return text;
  const next = { ...row, [field]: value };
  if (field === "role") { next.linked = true; next.people = roleCount(value, context); }
  if (field === "people") next.linked = false;
  const formula = [!next.linked ? next.people || "人数" : null, next.price || "单价"].filter(value => value !== null).join("×");
  const record = `${next.from.trim() || "出发城市"}-${next.to.trim() || "到达城市"} ${next.role || "角色"} ${formula}`;
  return replaceRecord(text, row, partsOf(row.record.text).map((part, index) => index === row.partIndex ? record : part.standalone));
}

/** 删除当前角色费用，保留同记录其他角色的路线。 */
export function removeTransportEntryRow(text: string, row: TransportEntryRow): string {
  return replaceRecord(text, row, partsOf(row.record.text).filter((_, index) => index !== row.partIndex).map(part => part.standalone));
}

/** 替换一条路线记录，保留其他记录并清理删空分隔符。 */
function replaceRecord(text: string, row: TransportEntryRow, records: string[]): string {
  if (records.length) return text.slice(0, row.record.start) + records.join("\n") + text.slice(row.record.end);
  let { start, end } = row.record;
  const after = text.slice(end).match(/^[\s;；]+/u)?.[0];
  if (after) end += after.length;
  else start -= text.slice(0, start).match(/[\s;；]*$/u)?.[0].length ?? 0;
  const result = text.slice(0, start) + text.slice(end);
  return /^[\s;；]*$/u.test(result) ? "" : result;
}

/** 新增城市路线与学生待补价行。 */
export function appendTransportEntryRow(text: string): string {
  return `${text}${text && !text.endsWith("\n") ? "\n" : ""}出发城市-到达城市 学生 单价`;
}
